import React from 'react';
import './VisibilityReportViewPage.scss';
import { useTranslation } from 'react-i18next';
import { canUseVisibilityReport } from '../../shared/helpers/addons/canUseVisibilityReport';
import {
  formatPeriodLabel,
  periodLocaleFor,
} from '../../shared/helpers/filters/formatPeriodLabel';
import { Navigate, useParams } from 'react-router-dom';
import Title from '../../shared/components/title/Title';
import Loader from '../../shared/components/common/loader/Loader';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { PROJECTS, REPORTS, SHARED_REPORT } from '../../shared/routes/path';
import { useGetVisibilityReport } from '../../shared/api/hooks/visibility-report/useGetVisibilityReport';
import { useGetProject } from '../../shared/api/hooks/project/useGetProject';
import { useShareVisibilityReport } from '../../shared/api/hooks/visibility-report/useShareVisibilityReport';
import { brandLabel } from '../../shared/components/report/brandLabel';
import { copyTextToClipboard } from '../../shared/utils/utils';
import { useDownloadVisibilityReportPdf } from '../../shared/api/hooks/visibility-report/useDownloadVisibilityReportPdf';
import VisibilityReportSlides from './components/VisibilityReportSlides';
import useToast from '../../shared/hooks/global/UseToast';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';

const parseId = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const VisibilityReportViewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { accountResponse } = useAccount();
  const { projectId: projectIdParam, reportId: reportIdParam } = useParams<{
    projectId: string;
    reportId: string;
  }>();
  const projectId = parseId(projectIdParam);
  const reportId = parseId(reportIdParam);

  const { data: report, isLoading, isError } = useGetVisibilityReport(reportId);
  // Projekt tylko po to, żeby poznać `ownerAddons` — to samo zapytanie, co na liście raportów.
  const { project } = useGetProject(projectId);
  const share = useShareVisibilityReport(projectId ?? 0);
  const { showToast, toastElement } = useToast();
  const isMobile = useIsMobile();
  // Przycisk PDF stoi w nagłówku strony, więc pobieranie trzyma strona, a nie okładka raportu.
  const { download, isDownloading } = useDownloadVisibilityReportPdf(report);
  const downloadPdf = () =>
    download().catch(() => showToast(t('visibilityReport.errorGeneric'), 'error'));

  // Patrz VisibilityReportsPage — ten sam warunek bramkuje listę i link w menu.
  const effectiveAddons = project?.ownerAddons ?? accountResponse?.addons ?? [];
  if (
    accountResponse &&
    !canUseVisibilityReport(effectiveAddons, accountResponse.isSuperAdmin)
  ) {
    return <Navigate to={PROJECTS} replace />;
  }

  if (projectId == null || reportId == null) {
    return <Navigate to={PROJECTS} replace />;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (isError || !report) {
    return (
      <div className="visibility-report-view page-content">
        <div className="page-box">{t('visibilityReport.notFound')}</div>
      </div>
    );
  }

  const brand = brandLabel(report.brand);
  const shareUrl = report.shareToken
    ? `${window.location.origin}${SHARED_REPORT}/${report.shareToken}`
    : '';

  const copyLink = async (url: string) => {
    const copied = await copyTextToClipboard(url);
    // Schowek bywa niedostępny (HTTP, uprawnienia przeglądarki) — wtedy mówimy o tym wprost,
    // zamiast udawać, że link jest skopiowany.
    showToast(copied ? t('copied') : t('visibilityReport.errorGeneric'), copied ? 'success' : 'error');
  };

  const handleShare = () => {
    if (shareUrl) {
      void copyLink(shareUrl);
      return;
    }
    // Udostępnienie i skopiowanie linku to dwie osobne decyzje — po włączeniu tylko potwierdzamy
    // toastem, a link użytkownik kopiuje tym samym przyciskiem, który zmienia się na „Copy link".
    share.mutate(
      { reportId, enabled: true },
      { onSuccess: () => showToast(t('visibilityReport.shareEnabled'), 'success') }
    );
  };

  return (
    <div className="visibility-report-view page-content">
      {toastElement}
      <div className="page-box">
        {/* Telefon: marka i wszystkie akcje (same ikony, PDF jako główna na niebiesko) w górnym pasku,
            pod nim tylko okres raportu. */}
        <MobilePageHeader
          title={<span>{brand}</span>}
          actions={
            <>
              {report.shareToken && (
                <button
                  type="button"
                  className="mobile-header__icon-btn"
                  disabled={share.isPending}
                  onClick={() => share.mutate({ reportId, enabled: false })}
                  aria-label={t('visibilityReport.shareRevoke')}
                >
                  <span className="ico--eye-off ico--sm" />
                </button>
              )}
              <button
                type="button"
                className="mobile-header__icon-btn"
                disabled={share.isPending}
                onClick={handleShare}
                aria-label={
                  report.shareToken ? t('visibilityReport.shareCopyLink') : t('visibilityReport.shareBtn')
                }
              >
                <span className="ico--copy ico--sm" />
              </button>
              <button
                type="button"
                className="btn btn--sm mobile-header__add"
                disabled={isDownloading}
                onClick={downloadPdf}
                aria-label={t('reportSavePdf')}
              >
                <span className="ico--download ico--xs" />
              </button>
            </>
          }
        />
        {isMobile ? (
          <div className="visibility-report-view__mobile-period">
            {formatPeriodLabel(report.periodFrom, report.periodTo, periodLocaleFor(i18n.language))}
          </div>
        ) : (
        <>
        {/* Nagłówek przykleja się do góry przy przewijaniu — ten sam mechanizm, co na
            podglądzie projektu: `page-box__filters` daje `position: sticky`, a globalny
            obserwator z `Panel` dokłada `data-stuck` (cień) po odklejeniu. */}
        <div className="page-box__filters" data-observe-sticky>
          <Title
            isBaseCategory={false}
            // Raport wisi pod `/reports/:projectId/:reportId`, ale należy do projektu — trail prowadzi
            // przez listę projektów i listę raportów TEGO projektu, nie przez segmenty adresu.
            breadcrumbs={[
              { name: t('projects'), href: PROJECTS },
              { name: t('visibilityReport.title'), href: `${REPORTS}/${projectId}` },
            ]}
            title={brand}
            childrenOnMain={
              <div className="title-box__subtitle">
                {formatPeriodLabel(report.periodFrom, report.periodTo, periodLocaleFor(i18n.language))}
              </div>
            }
          >
            {/* Bez osobnego wskaźnika „Shared publicly" — o tym, że raport jest udostępniony, mówi
                już sam przycisk kopiowania linku i sąsiadujące z nim „Stop sharing". */}
            {/* Kolejność od czynności najrzadszej do najczęstszej: wycofanie udostępnienia, potem
                kopiowanie linku, na końcu pobranie PDF-a — akcja główna, więc jako jedyna pełnym
                kolorem. Raport prywatny nie ma pierwszego przycisku i zostaje para share + PDF. */}
            <div className="filters">
              {report.shareToken && (
                <button
                  type="button"
                  className="btn btn--sm btn--red"
                  disabled={share.isPending}
                  onClick={() => share.mutate({ reportId, enabled: false })}
                >
                  <span className="ico--eye-off ico--xs" aria-hidden="true" />
                  {t('visibilityReport.shareRevoke')}
                </button>
              )}
              <button
                type="button"
                className="btn btn--sm btn--secondary"
                disabled={share.isPending}
                onClick={handleShare}
              >
                <span className="ico--copy ico--xs" aria-hidden="true" />
                {report.shareToken
                  ? t('visibilityReport.shareCopyLink')
                  : t('visibilityReport.shareBtn')}
              </button>
              <button
                type="button"
                className="btn btn--sm"
                disabled={isDownloading}
                onClick={downloadPdf}
              >
                {/* Bez podmiany na spinner — ikona zostaje ta sama, a sygnałem pracy jest samo
                    zablokowanie przycisku na czas generowania. */}
                <span className="ico--download ico--xs" aria-hidden="true" />
                {t('reportSavePdf')}
              </button>
            </div>
          </Title>
        </div>
        </>
        )}

        <div className="visibility-report-view__canvas">
          <div className="visibility-report-view__report">
            <VisibilityReportSlides report={report} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisibilityReportViewPage;
